import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articlesList } from "@/mocks/articles";
import { articleContent } from "@/mocks/articleContent";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://vigorbuy.quest";

const articleImages: Record<string, string> = {
  "article-01": "https://readdy.ai/api/search-image?query=Open%20laptop%20displaying%20a%20colorful%20organized%20spreadsheet%20on%20screen%20beside%20a%20steaming%20ceramic%20coffee%20mug%20and%20a%20small%20potted%20succulent%20plant%2C%20clean%20white%20desk%2C%20bright%20natural%20window%20light%2C%20productive%20morning%20workspace%20vibe%2C%20editorial%20tech%20lifestyle%20photography%20with%20warm%20atmosphere&width=1200&height=600&seq=article-hero-01-v3&orientation=landscape",
  "article-02": "https://readdy.ai/api/search-image?query=Magnifying%20glass%20hovering%20over%20a%20product%20tag%20with%20a%20verified%20seller%20checkmark%20icon%2C%20warm%20wooden%20table%20surface%2C%20scattered%20fashion%20items%20slightly%20out%20of%20focus%20in%20background%2C%20detective-style%20concept%2C%20soft%20directional%20lighting%2C%20trust%20and%20verification%20theme%20photography&width=1200&height=600&seq=article-hero-02-v3&orientation=landscape",
  "article-03": "https://readdy.ai/api/search-image?query=Hands%20holding%20a%20smartphone%20closely%20examining%20detailed%20product%20photos%20on%20screen%2C%20colorful%20garment%20visibly%20out%20of%20focus%20behind%20the%20phone%2C%20bright%20daylight%20studio%20setting%2C%20quality%20inspection%20concept%2C%20crisp%20macro%20product%20photography%2C%20attention%20to%20detail%20theme&width=1200&height=600&seq=article-hero-03-v3&orientation=landscape",
  "article-04": "https://readdy.ai/api/search-image?query=Cardboard%20shipping%20boxes%20stacked%20near%20a%20world%20map%20on%20a%20wall%2C%20one%20box%20open%20revealing%20neatly%20wrapped%20products%20with%20bubble%20wrap%2C%20warm%20afternoon%20light%20from%20window%2C%20global%20shipping%20logistics%20concept%2C%20organized%20e-commerce%20fulfillment%20photography&width=1200&height=600&seq=article-hero-04-v3&orientation=landscape",
  "article-05": "https://readdy.ai/api/search-image?query=Colorful%20grid%20display%20of%20ten%20different%20fashion%20product%20types%20including%20shoes%20bags%20caps%20and%20accessories%20on%20individual%20white%20pedestals%2C%20gallery%20exhibition%20lighting%2C%20curated%20museum-style%20product%20arrangement%2C%20vibrant%20diverse%20collection%2C%20modern%20retail%20aesthetic%20photography&width=1200&height=600&seq=article-hero-05-v3&orientation=landscape",
  "article-06": "https://readdy.ai/api/search-image?query=Split%20screen%20comparison%20showing%20messy%20scattered%20paper%20notes%20and%20receipts%20on%20left%20side%20versus%20organized%20digital%20tablet%20with%20clean%20spreadsheet%20interface%20on%20right%20side%2C%20minimalist%20white%20desk%2C%20concept%20photography%2C%20sharp%20contrast%20between%20chaos%20and%20order&width=1200&height=600&seq=article-hero-06-v3&orientation=landscape",
  "article-07": "https://readdy.ai/api/search-image?query=Glass%20jar%20filled%20with%20stacked%20coins%20and%20a%20smartphone%20beside%20it%20showing%20price%20comparison%20charts%2C%20green%20plant%20in%20background%2C%20savings%20and%20smart%20shopping%20concept%2C%20bright%20natural%20daylight%2C%20modern%20personal%20finance%20aesthetic%2C%20optimistic%20warm%20tones%20photography&width=1200&height=600&seq=article-hero-07-v3&orientation=landscape",
  "article-08": "https://readdy.ai/api/search-image?query=Red%20warning%20sign%20icons%20floating%20above%20confused%20person%20looking%20at%20a%20cluttered%20smartphone%20screen%2C%20clean%20studio%20background%2C%20beginner%20mistakes%20concept%20illustration%2C%20soft%20even%20lighting%2C%20educational%20cautionary%20visual%20metaphor%2C%20approachable%20modern%20style&width=1200&height=600&seq=article-hero-08-v3&orientation=landscape",
  "article-09": "https://readdy.ai/api/search-image?query=Large%20glowing%20question%20mark%20hologram%20floating%20above%20an%20open%20laptop%20on%20a%20dark%20desk%20in%20a%20dimly%20lit%20room%2C%20futuristic%20tech%20support%20aesthetic%2C%20blue%20and%20warm%20amber%20contrasting%20lighting%2C%20FAQ%20and%20help%20concept%2C%20cinematic%20moody%20photography%20with%20dramatic%20atmosphere&width=1200&height=600&seq=article-hero-09-v3&orientation=landscape",
  "article-10": "https://readdy.ai/api/search-image?query=Rocket%20ship%20launching%20from%20an%20open%20laptop%20screen%20with%20digital%20sparks%20and%20light%20trails%20against%20a%20starry%20night%20backdrop%20visible%20through%20a%20window%2C%20innovation%20and%20future%20concept%2C%20creative%20surreal%20digital%20art%20style%2C%20inspiring%20and%20forward-looking%20aesthetic%20with%20cosmic%20colors&width=1200&height=600&seq=article-hero-10-v3&orientation=landscape",
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  const article = articlesList.find((a) => a.slug === slug);
  const content = slug ? articleContent[slug] : undefined;

  if (!article || !content) {
    return (
      <>
        <Navbar />
        <main className="w-full min-h-screen flex items-center justify-center pt-20">
          <div className="text-center px-4">
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-4">Article Not Found</h1>
            <p className="text-foreground-600 mb-6">The article you are looking for does not exist or has been moved.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i> Back to Home
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const imageKey = article.seq;
  const articleUrl = `${siteUrl}/article/${article.slug}`;

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.excerpt,
            datePublished: "2026-06-28",
            dateModified: article.date,
            author: {
              "@type": "Organization",
              name: "VigorBuy Team",
            },
            publisher: {
              "@type": "Organization",
              name: "VigorBuy",
              url: siteUrl,
            },
            url: articleUrl,
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": articleUrl,
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: `${siteUrl}/`,
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Guides",
                item: `${siteUrl}/#articles`,
              },
              {
                "@type": "ListItem",
                position: 3,
                name: article.title,
              },
            ],
          }),
        }}
      />
      <Navbar />
      <main>
        <section className="w-full pt-24 pb-10 md:pt-28 md:pb-14 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap">
              <Link to="/" className="hover:text-accent-600 transition-colors font-medium">
                Home
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/#articles" className="hover:text-accent-600 transition-colors font-medium">
                Guides
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700 font-semibold truncate max-w-[200px]">{article.title}</span>
            </div>

            <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-semibold rounded-full mb-4">
              {article.category}
            </span>

            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 mt-4 text-xs text-foreground-500">
              <span>{article.date}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span>{article.readTime}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span>By VigorBuy Team</span>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 pb-8">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="w-full h-56 md:h-80 rounded-xl overflow-hidden">
              <img
                src={articleImages[imageKey]}
                alt={article.title}
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
          </div>
        </section>

        <article className="w-full py-8 md:py-12 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div
              className="prose-custom text-foreground-800 text-sm md:text-base leading-relaxed space-y-6"
              dangerouslySetInnerHTML={{ __html: content.body }}
            />

            <div className="mt-12 p-6 md:p-8 bg-accent-50 border border-accent-200 rounded-xl text-center">
              <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">
                Ready to Explore the VigorBuy Spreadsheet?
              </h3>
              <p className="text-sm text-foreground-600 mb-5 max-w-md mx-auto">
                Access the latest version with 5000+ products, verified sellers, and weekly updates — completely free.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-6 py-2.5 bg-accent-500 text-background-50 text-sm font-semibold rounded-full hover:bg-accent-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Access VigorBuy Sheet
                </a>
                <Link
                  to="/"
                  className="px-6 py-2.5 border border-foreground-300 text-foreground-700 text-sm font-medium rounded-full hover:bg-background-100 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  ← Back to Home
                </Link>
              </div>
            </div>

            <div className="mt-12 pt-8 border-t border-background-200">
              <h3 className="font-heading text-lg font-bold text-foreground-950 mb-5">More VigorBuy Guides</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {articlesList
                  .filter((a) => a.slug !== slug)
                  .slice(0, 4)
                  .map((a) => (
                    <Link
                      key={a.slug}
                      to={`/article/${a.slug}`}
                      className="flex items-center gap-3 p-3 rounded-lg bg-background-100 hover:bg-background-200/70 transition-colors group cursor-pointer"
                    >
                      <span className="w-2 h-2 rounded-full bg-accent-500 flex-shrink-0"></span>
                      <span className="text-sm text-foreground-700 group-hover:text-accent-600 transition-colors leading-snug">
                        {a.title}
                      </span>
                    </Link>
                  ))}
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-background-200">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-accent-600 transition-colors"
              >
                <i className="ri-arrow-left-line"></i>
                Back to VigorBuy Home
              </Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}